// components/OnboardingTour.jsx — Anamoria SPA
// v1.0 — App walkthrough onboarding tour (May 22, 2026)
//
// Tour controller component. Renders an SVG spotlight overlay with a
// rounded cutout around each target element, plus a positioned tooltip
// card with step content, navigation, and accessibility features.
//
// Architecture:
//   - Targets found via data-tour="stepId" attributes (standard pattern
//     per Shepherd.js, React Joyride, Intro.js — inert strings, zero
//     runtime cost when tour is inactive).
//   - SVG overlay with <path> cutout for true transparent spotlight
//     (target remains visible and clickable). Shepherd.js pattern.
//   - Tooltip positioned relative to target (above/below/left/right).
//   - Mobile (≤768px): tooltip renders as bottom sheet.
//   - Focus trap: Tab/Shift+Tab cycle within tooltip. Escape dismisses.
//   - aria-live="polite" on tooltip content for screen reader announcements.
//   - All buttons ≥ 24px tap target (WCAG 2.2 §2.5.8).
//   - Contrast: sage-dark (#5b7a65) on white = 4.64:1 (WCAG AA pass).
//
// Props:
//   steps     — array of { target, title, content, position } objects
//   onComplete — called when user finishes all steps ("Done" on last step)
//   onSkip     — called when user clicks "Skip tour"
//   getApi     — API client factory for PATCH /pilot/onboarding
//
// Best practice sources:
//   WCAG 2.2 §2.1.2 (No Keyboard Trap), §2.4.3 (Focus Order),
//   §2.5.8 (Target Size), §4.1.3 (Status Messages), §1.4.3 (Contrast).
//   Pendo (2025), Userpilot (2026), Shepherd.js SVG overlay pattern.

import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './OnboardingTour.module.css';

// ─── SVG path for overlay with rounded-rect cutout ────────────────────────
// Creates a full-viewport filled path with a hole matching the target rect.
// Outer rect: 0,0 → viewportW,viewportH (clockwise)
// Inner rect: rounded rectangle (counter-clockwise to create the hole)

function buildSpotlightPath(rect, padding = 8, radius = 12) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Target bounds with padding
  const x = rect.left - padding;
  const y = rect.top - padding;
  const w = rect.width + padding * 2;
  const h = rect.height + padding * 2;
  const r = Math.min(radius, w / 2, h / 2);

  // Outer rectangle (clockwise)
  // Inner rounded rectangle (counter-clockwise = hole)
  return [
    `M0,0 H${vw} V${vh} H0 Z`,
    `M${x + r},${y}`,
    `H${x + w - r}`,
    `A${r},${r} 0 0 1 ${x + w},${y + r}`,
    `V${y + h - r}`,
    `A${r},${r} 0 0 1 ${x + w - r},${y + h}`,
    `H${x + r}`,
    `A${r},${r} 0 0 1 ${x},${y + h - r}`,
    `V${y + r}`,
    `A${r},${r} 0 0 1 ${x + r},${y}`,
    'Z',
  ].join(' ');
}

// ─── Tooltip positioning ──────────────────────────────────────────────────
// Calculates tooltip position relative to the target element.
// Returns { top, left } in pixels. Clamps to viewport edges.

function getTooltipPosition(targetRect, tooltipRect, position, padding = 16) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top, left;

  switch (position) {
    case 'above':
      top = targetRect.top - tooltipRect.height - padding;
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
      break;
    case 'below':
      top = targetRect.bottom + padding;
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
      break;
    case 'left':
      top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
      left = targetRect.left - tooltipRect.width - padding;
      break;
    case 'right':
      top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
      left = targetRect.right + padding;
      break;
    default:
      top = targetRect.bottom + padding;
      left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
  }

  // Clamp to viewport with margin
  const margin = 12;
  left = Math.max(margin, Math.min(left, vw - tooltipRect.width - margin));
  top = Math.max(margin, Math.min(top, vh - tooltipRect.height - margin));

  return { top, left };
}

export default function OnboardingTour({ steps, onComplete, onSkip, getApi }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [completing, setCompleting] = useState(false);

  const tooltipRef = useRef(null);
  const preTourFocusRef = useRef(null);
  const overlayRef = useRef(null);

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  // ─── Save pre-tour focus for restoration on dismiss ───────────────
  useEffect(() => {
    preTourFocusRef.current = document.activeElement;
    return () => {
      // Restore focus when tour unmounts
      if (preTourFocusRef.current && preTourFocusRef.current.focus) {
        preTourFocusRef.current.focus();
      }
    };
  }, []);

  // ─── Find target element and calculate positions ──────────────────
  const recalculate = useCallback(() => {
    if (!step) return;

    const target = document.querySelector(`[data-tour="${step.target}"]`);
    if (!target) {
      // Target not found — skip this step gracefully
      if (!isLast) {
        setCurrentStep(prev => prev + 1);
      }
      return;
    }

    const rect = target.getBoundingClientRect();
    setTargetRect(rect);

    // Calculate tooltip position after next render (need tooltip dimensions)
    requestAnimationFrame(() => {
      if (tooltipRef.current && !isMobile) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const pos = getTooltipPosition(rect, tooltipRect, step.position);
        setTooltipPos(pos);
      }
    });
  }, [step, currentStep, isMobile, isLast]);

  useEffect(() => {
    recalculate();
  }, [recalculate]);

  // ─── Resize + scroll listener ─────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      recalculate();
    };

    const handleScroll = () => {
      recalculate();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [recalculate]);

  // ─── Focus management — move focus to tooltip on step change ──────
  useEffect(() => {
    if (tooltipRef.current) {
      tooltipRef.current.focus();
    }
  }, [currentStep]);

  // ─── Keyboard handler — focus trap + Escape ───────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
        return;
      }

      if (e.key === 'Tab') {
        // Focus trap within tooltip
        const tooltip = tooltipRef.current;
        if (!tooltip) return;

        const focusable = tooltip.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, completing]);

  // ─── Complete / skip handler ──────────────────────────────────────
  const persistComplete = useCallback(async () => {
    if (completing) return;
    setCompleting(true);
    try {
      const api = getApi();
      await api.patch('/pilot/onboarding');
    } catch (err) {
      // Non-blocking — tour completion is UX, not critical.
      // User can re-trigger from Settings if needed.
      console.warn('Failed to persist onboarding completion:', err.message);
    }
  }, [getApi, completing]);

  const handleNext = useCallback(() => {
    if (isLast) {
      persistComplete().then(() => onComplete());
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [isLast, onComplete, persistComplete]);

  const handleBack = useCallback(() => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirst]);

  const handleSkip = useCallback(() => {
    persistComplete().then(() => onSkip());
  }, [onSkip, persistComplete]);

  // ─── Render ───────────────────────────────────────────────────────

  if (!step || !targetRect) return null;

  const spotlightPath = buildSpotlightPath(targetRect);

  return (
    <div className={styles.tourContainer} role="dialog" aria-modal="true" aria-label="App tour">

      {/* SVG overlay with spotlight cutout */}
      <svg
        ref={overlayRef}
        className={styles.overlay}
        viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d={spotlightPath}
          fill="rgba(0, 0, 0, 0.5)"
          fillRule="evenodd"
        />
      </svg>

      {/* Tooltip — positioned or bottom-sheet */}
      <div
        ref={tooltipRef}
        className={`${styles.tooltip} ${isMobile ? styles.tooltipMobile : ''}`}
        style={isMobile ? undefined : { top: tooltipPos.top, left: tooltipPos.left }}
        tabIndex={-1}
        role="document"
      >
        {/* Step counter */}
        <div className={styles.stepCounter} aria-hidden="true">
          {currentStep + 1} of {steps.length}
        </div>

        {/* Content — aria-live for screen reader announcements (BP-8) */}
        <div aria-live="polite" aria-atomic="true">
          <h3 className={styles.tooltipTitle}>{step.title}</h3>
          <p className={styles.tooltipContent}>{step.content}</p>
        </div>

        {/* Navigation footer */}
        <div className={styles.tooltipFooter}>
          <div className={styles.footerLeft}>
            {!isFirst && (
              <button
                className={styles.backBtn}
                onClick={handleBack}
                type="button"
              >
                Back
              </button>
            )}
            <button
              className={styles.skipBtn}
              onClick={handleSkip}
              type="button"
            >
              Skip tour
            </button>
          </div>
          <button
            className={styles.nextBtn}
            onClick={handleNext}
            disabled={completing}
            type="button"
          >
            {isLast ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
